import { useInfiniteQuery } from 'react-query';
import { baseApiUrl, ServerErrorResponse, fetcher, createParamString } from 'data_sources/constants';
import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import { ApiCollectionResponse } from 'types/api_response';

export default function useGroupUsers<T extends User>(
  organizationId: string,
  groupId: string,
  params = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexid/user-group/v1/organizations/${organizationId}/groups/${groupId}/users`;
  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['group_users', organizationId, groupId],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
